import { FileText, Search, ScrollText, KeyRound } from "lucide-react";

const STEPS = [
  {
    num: "01",
    icon: FileText,
    title: "Submit your information",
    desc:
      "Complete the form below with high-level details about your business. It takes about 3 minutes and stays strictly confidential.",
  },
  {
    num: "02",
    icon: Search,
    title: "Initial review",
    desc:
      "If there's a fit, we'll reach out and request 3 years of P&L statements and 3 years of tax returns to begin diligence.",
  },
  {
    num: "03",
    icon: ScrollText,
    title: "Valuation & offer",
    desc:
      "We complete a fair valuation — typically 3–5× earnings — and present a structured offer you can actually understand.",
  },
  {
    num: "04",
    icon: KeyRound,
    title: "Closing",
    desc:
      "With financing already secured, we move to a swift, well-supported closing and a transition plan that protects your team.",
  },
];

export default function Process() {
  return (
    <section
      id="how-it-works"
      data-testid="process-section"
      className="py-24 md:py-32 bg-[#F3EFE9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <span className="eyebrow">Our acquisition process</span>
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight mt-5 text-[#1A2621]">
            Four steps.
            <span className="italic text-[#284A3B]"> No surprises.</span>
          </h2>
          <p className="mt-6 text-lg text-[#4A5D54] leading-relaxed">
            We&rsquo;ve refined our process so owners always know what comes
            next — what we&rsquo;ll need from you, and what you can expect
            from us.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 gap-6 md:gap-8">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div
                key={step.num}
                data-testid={`process-step-${step.num}`}
                className="relative bg-[#FDFBF7] rounded-2xl border border-[#D5DACF] p-8 md:p-10 transition-all hover:-translate-y-1 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <span className="font-serif-display text-5xl text-[#C86A51]/80 leading-none">
                    {step.num}
                  </span>
                  <span className="w-11 h-11 rounded-full bg-[#E6EAE3] flex items-center justify-center text-[#284A3B]">
                    <Icon size={18} />
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-8 text-[#1A2621] leading-snug">
                  {step.title}
                </h3>
                <p className="mt-4 text-[#4A5D54] leading-relaxed">
                  {step.desc}
                </p>
              </div>
            );
          })}
        </div>

        <div
          data-testid="requirements-callout"
          className="mt-12 rounded-2xl bg-[#284A3B] text-[#FDFBF7] p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        >
          <div>
            <div className="eyebrow text-[#C86A51]">What you&rsquo;ll need</div>
            <h3 className="font-serif-display text-2xl md:text-3xl mt-3">
              3 years of P&amp;L statements · 3 years of tax returns
            </h3>
            <p className="mt-3 text-[#FDFBF7]/80 max-w-2xl">
              No need to send anything yet — we only request documents after
              an initial conversation and signed mutual confidentiality.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
